import { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import type { ButtonThemeValues } from 'ui-components/components/button/button-types';

import { TRANSFER_ORDER_ACTION_RECEIVE_CLICKED } from '../../../../constants/analytics-events';
import { useModal } from '../../../../hooks/use-modal';
import { useSendMetrics } from '../../../../hooks/use-send-metrics';
import { useTransferOrdersGridContext } from '../data/transfer-orders-context';
import { ReceiveTransferOrderDrawer } from './receive-transfer-order-drawer';
import { TransferOrderReceivedProductsProvider } from './transfer-order-receiving-context';
import { useWarehouseLocations } from './use-warehouse-locations';
import type { TransferOrderRow } from '../data/transfer-orders-context.types';

type ReceiveTransferOrderButtonProps = {
  transferOrder: TransferOrderRow;
  buttonMessage: MessageDescriptor;
  className?: string;
  theme: ButtonThemeValues;
  disabled: boolean;
  onSuccess?: () => void;
};

export const ReceiveTransferOrderButton = ({
  transferOrder,
  buttonMessage,
  className = '',
  theme,
  disabled,
  onSuccess,
}: ReceiveTransferOrderButtonProps) => {
  const { isOpen, open, close } = useModal();
  const warehouseId = transferOrder?.destinationWarehouseId;
  const warehouseLocations = useWarehouseLocations(warehouseId);
  const { refreshTransferOrders } = useTransferOrdersGridContext();
  const trackMetrics = useSendMetrics();

  const handleOpen = useCallback(() => {
    open();
    trackMetrics(TRANSFER_ORDER_ACTION_RECEIVE_CLICKED, { transferOrderId: transferOrder?.transferOrderId });
  }, [open, transferOrder?.transferOrderId, trackMetrics]);

  const handleSuccess = useCallback(() => {
    refreshTransferOrders();
    onSuccess?.();
  }, [refreshTransferOrders, onSuccess]);

  return (
    <>
      <Button className={className} data-testid='receive-transfer-order-button' disabled={disabled} onClick={handleOpen} theme={theme}>
        <Text textType={TextType.Label} value={buttonMessage} />
      </Button>

      {isOpen ? (
        <TransferOrderReceivedProductsProvider transferOrderId={transferOrder?.transferOrderId} warehouseLocations={warehouseLocations}>
          <ReceiveTransferOrderDrawer
            onClose={close}
            onSuccess={handleSuccess}
            transferOrder={transferOrder}
            warehouseLocations={warehouseLocations}
          />
        </TransferOrderReceivedProductsProvider>
      ) : null}
    </>
  );
};
